import { useNavigate } from "@tanstack/react-router";
import { useTranslate } from "@tolgee/react";
import { useAtomValue, useSetAtom } from "jotai";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";
import { LocalizedLink } from "#/components/locale/localized-link";
import { Markdown } from "#/components/ui/markdown";
import { PageLoader } from "#/components/ui/page-loader";
import { TableErrorState } from "#/components/ui/table-error-state";
import { linkifyTradeSymbols } from "#/features/trade/lib/trade-symbol-links";
import { analytics } from "#/lib/analytics";
import type { Post } from "#/lib/api/posts";
import { learnSeoAtom } from "#/stores/learn-seo-store";
import { localeAtom } from "#/stores/locale-store";
import { RELATED_SLUGS } from "../content";
import { useLearnPost } from "../hooks/use-learn-post";
import { useLearnPosts } from "../hooks/use-learn-posts";

// Articles whose subject matter has a product surface to hand off to — the
// tenbagger pieces end with a CTA into the /baggers multiples ranking.
const BAGGERS_CTA_SLUGS = [
	"what-is-a-tenbagger",
	"how-to-find-tenbaggers",
	"tenbagger-case-sk-hynix",
	"leverage-tenbagger-math",
	"tenbagger-case-wti-oil",
];

export function LearnArticlePage({ slug }: { slug: string }) {
	const { t } = useTranslate();
	const locale = useAtomValue(localeAtom);
	const navigate = useNavigate();
	const postQuery = useLearnPost(slug);
	const listQuery = useLearnPosts();
	const post = postQuery.data;

	// 기존 이벤트와 동일한 값 형태 유지 (section = learn.section.<slug> 문자열)
	const section = post ? `learn.section.${post.category}` : null;
	useEffect(() => {
		if (!section) return;
		analytics.track("learn_article_viewed", { slug, section, locale });
	}, [slug, section, locale]);

	// fetch 결과를 SEO 심에 게시 — SeoSync가 title/meta/JSON-LD를 갱신한다.
	// post === undefined(로딩)는 게시하지 않고, null(404)은 noindex 분기용으로 게시.
	const setLearnSeo = useSetAtom(learnSeoAtom);
	useEffect(() => {
		if (post === undefined) return;
		setLearnSeo({ slug, post });
		return () => setLearnSeo(null);
	}, [slug, post, setLearnSeo]);

	if (postQuery.isPending) {
		return (
			<div className="mx-auto max-w-3xl p-4 pt-10 pb-16 min-h-[70vh]">
				<BackLink label={t("learn.guide.back")} />
				<PageLoader />
			</div>
		);
	}

	if (postQuery.isError) {
		return (
			<div className="mx-auto max-w-3xl p-4 pt-10 pb-16 min-h-[70vh]">
				<BackLink label={t("learn.guide.back")} />
				<TableErrorState
					className="min-h-[50vh]"
					onRetry={() => postQuery.refetch()}
				/>
			</div>
		);
	}

	// null = 미발행/없는 slug — 예상된 상태 (fetchPost가 404를 null로 매핑)
	if (!post) {
		return (
			<div className="mx-auto max-w-3xl p-4 pt-10 pb-16 min-h-[70vh]">
				<BackLink label={t("learn.guide.back")} />
				<h1 className="mt-6 text-title1 text-foreground">
					{t("learn.guide.not-found")}
				</h1>
			</div>
		);
	}

	const relatedPosts = (RELATED_SLUGS[slug] ?? [])
		.map((relatedSlug) =>
			listQuery.data?.find((candidate) => candidate.slug === relatedSlug),
		)
		.filter((candidate): candidate is Post => Boolean(candidate));

	return (
		<article className="mx-auto max-w-3xl p-4 pt-10 pb-16 min-h-[70vh]">
			<BackLink label={t("learn.guide.back")} />

			<div className="mt-6 mb-2 text-label2-bold uppercase text-muted-foreground">
				{t(`learn.section.${post.category}`)}
			</div>

			<header className="mb-8">
				<h1 className="text-title1 text-foreground whitespace-pre-line">
					{post.title}
				</h1>
			</header>

			{/* Symbol mentions (e.g. WTIOIL-PERP) become /trade links at render
			    time — the CMS bodies stay plain text. Click tracking AND SPA
			    navigation are delegated here: the Lexical renderer emits plain
			    <a> tags, so router navigation is intercepted at the wrapper. */}
			<div
				className="learn-guide-body"
				onClickCapture={(event) => {
					const anchor = (event.target as HTMLElement).closest?.(
						'a[href^="/trade/"]',
					);
					const symbol = anchor?.getAttribute("href")?.slice("/trade/".length);
					if (!symbol) return;
					analytics.track("market_selected", {
						symbol,
						source: "learn-article",
						dex: symbol.startsWith("xyz-") ? "xyz" : "main",
					});
					// 보조 클릭(새 탭 등)은 브라우저 기본 동작 유지
					if (
						event.metaKey ||
						event.ctrlKey ||
						event.shiftKey ||
						event.altKey ||
						event.button !== 0
					) {
						return;
					}
					event.preventDefault();
					navigate({ to: "/trade/$symbol", params: { symbol } });
				}}
			>
				<Markdown>{linkifyTradeSymbols(post.body)}</Markdown>
			</div>

			{relatedPosts.length > 0 && (
				<section className="mt-10 border-t border-border pt-8">
					<h2 className="mb-4 text-title1 text-foreground">
						{t("learn.guide.related-heading")}
					</h2>
					<ul className="flex flex-col gap-2">
						{relatedPosts.map((related) => (
							<li key={related.slug}>
								<LocalizedLink
									to="/learn/$slug"
									params={{ slug: related.slug }}
									className="text-body2 text-foreground hover:underline"
								>
									{related.title}
								</LocalizedLink>
							</li>
						))}
					</ul>
				</section>
			)}

			{BAGGERS_CTA_SLUGS.includes(slug) && (
				<section className="mt-10 rounded-lg border border-border bg-muted/30 p-6">
					<h2 className="text-title1 text-foreground">
						{t("learn.cta.baggers.title")}
					</h2>
					<p className="mt-1 text-body2 text-muted-foreground">
						{t("learn.cta.baggers.description")}
					</p>
					<LocalizedLink
						to="/baggers"
						onClick={() =>
							analytics.track("learn_cta_clicked", { slug, locale })
						}
						className="mt-4 inline-flex items-center gap-1 rounded-md bg-primary px-4 py-2 text-body2 text-primary-foreground hover:opacity-90"
					>
						{t("learn.cta.baggers.button")} →
					</LocalizedLink>
				</section>
			)}
		</article>
	);
}

function BackLink({ label }: { label: string }) {
	return (
		<LocalizedLink
			to="/learn"
			className="inline-flex items-center gap-1 text-body2 text-muted-foreground hover:text-foreground"
		>
			<ArrowLeft className="h-4 w-4" />
			{label}
		</LocalizedLink>
	);
}
